// client/src/components/billingExecution/BillingReceiptsList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CheckCircle as ApprovedIcon,
  Cancel as NotApprovedIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingReceiptService from '../../services/billingReceiptService';

const BillingReceiptsList = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [downloadOptionsDialogOpen, setDownloadOptionsDialogOpen] = useState(false);
  const [receiptToDownload, setReceiptToDownload] = useState(null);
  
  // Current language
  const lang = i18n.language;

  // Check if user is checker or has both roles
  const canReview = isAdmin() || 
                    (user.role === 'faculty' && 
                    (user.user_type === 'checker' || user.user_type === 'both'));
  
  // Load receipts on component mount
  useEffect(() => {
    loadReceipts();
  }, []);
  
  // Function to load receipts
  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await billingReceiptService.getBillingReceipts();
      setReceipts(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingReceipts'));
    } finally {
      setLoading(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return formatCurrency(0);
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(parseFloat(value));
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Handle delete click
  const handleDeleteClick = (receipt) => {
    setReceiptToDelete(receipt);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      await billingReceiptService.deleteBillingReceipt(receiptToDelete.id);
      setSuccess(t('receiptDeletedSuccess'));
      loadReceipts();
    } catch (err) {
      setError(t('errorDeletingReceipt'));
    } finally {
      setDeleteDialogOpen(false);
      setReceiptToDelete(null);
    }
  };
  
  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReceiptToDelete(null);
  };
  
  // Handle download click
  const handleDownloadClick = (receipt) => {
    // If not approved and not the creator or checker, show error
    if (!receipt.is_approved && 
        receipt.created_by !== user.id && 
        !canReview) {
      setError(t('cannotDownloadUnapprovedReceipt'));
      return;
    }
    
    setReceiptToDownload(receipt);
    setDownloadOptionsDialogOpen(true);
  };
  
  // Handle download options dialog close
  const handleDownloadOptionsClose = () => {
    setDownloadOptionsDialogOpen(false);
    setReceiptToDownload(null);
  };
  
  // Handle download excel (detailed)
  const handleDownloadDetailed = async () => {
    try {
      await billingReceiptService.downloadReceiptExcel(receiptToDownload.id, false);
      handleDownloadOptionsClose();
    } catch (err) {
      setError(t('errorDownloadingExcel'));
    }
  };
  
  // Handle download excel (summarized)
  const handleDownloadSummarized = async () => {
    try {
      await billingReceiptService.downloadReceiptExcel(receiptToDownload.id, true);
      handleDownloadOptionsClose();
    } catch (err) {
      setError(t('errorDownloadingExcel'));
    }
  };
  
  // Handle download distribution (detailed)
  const handleDownloadDistributionDetailed = async () => {
    try {
      await billingReceiptService.downloadReceiptDistributionExcel(receiptToDownload.id, false);
      handleDownloadOptionsClose();
    } catch (err) {
      setError(t('errorDownloadingDistributionExcel'));
    }
  };
  
  // Handle download distribution (summarized)
  const handleDownloadDistributionSummarized = async () => {
    try {
      await billingReceiptService.downloadReceiptDistributionExcel(receiptToDownload.id, true);
      handleDownloadOptionsClose();
    } catch (err) {
      setError(t('errorDownloadingDistributionExcel'));
    }
  };
  
  // Handle close alert
  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };
  
  // Calculate total value from a receipt
  const calculateTotalValue = (receipt) => {
    if (!receipt.transaction || !receipt.items || receipt.items.length === 0) {
      return 0;
    }
    
    const baseTotal = parseFloat(receipt.transaction.total_value || 0) * receipt.items.length;
    const additionalTotal = receipt.items.reduce((sum, item) => sum + parseFloat(item.additional_value || 0), 0);
    
    return baseTotal + additionalTotal;
  };
  
  // Check if user can delete a receipt
  const canDelete = (receipt) => {
    // Admins can delete any receipt
    if (isAdmin()) return true;
    
    // Faculty users can only delete:
    // 1. Receipts they created
    // 2. Only if they are makers or both
    // 3. Only if the receipt is not approved
    if (user.role === 'faculty') {
      return receipt.created_by === user.id && 
             (user.user_type === 'maker' || user.user_type === 'both') && 
             !receipt.is_approved;
    }
    
    return false;
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('billingReceipts')}
      </Typography>
      
      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Add Receipt button - only for makers or both */}
      {(isAdmin() || user.user_type === 'maker' || user.user_type === 'both') && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/billing-execution"
          >
            {t('createNewReceipt')}
          </Button>
        </Box>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : receipts.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noReceiptsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('id')}</TableCell>
                <TableCell>{t('transactionName')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell>{t('creationDate')}</TableCell>
                <TableCell align="center">{t('students')}</TableCell>
                <TableCell align="right">{t('totalValue')}</TableCell>
                <TableCell align="center">{t('approval')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell>{receipt.id}</TableCell>
                  <TableCell>{receipt.transaction?.paymentItem?.name || t('unknown')}</TableCell>
                  <TableCell>
                    {receipt.userFaculty ? receipt.userFaculty[`name_${lang}`] : t('unknown')}
                  </TableCell>
                  <TableCell>{formatDate(receipt.created_at)}</TableCell>
                  <TableCell align="center">{receipt.items?.length || 0}</TableCell>
                  <TableCell align="right">{formatCurrency(calculateTotalValue(receipt))}</TableCell>
                  <TableCell align="center">
                    {receipt.is_approved ? (
                      <Tooltip title={`${t('approvedBy')}: ${receipt.approver?.username || t('unknown')}`}>
                        <Chip 
                          icon={<ApprovedIcon />}
                          label={t('approved')} 
                          color="success" 
                          size="small"
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('notApprovedYet')}>
                        <Chip 
                          icon={<NotApprovedIcon />}
                          label={t('notApproved')} 
                          color="warning" 
                          size="small"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={t(receipt.status)}
                      color={getStatusColor(receipt.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('view')}>
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/billing-receipts/preview/${receipt.id}`}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Review button - only for checkers or both and only for unapproved receipts */}
                    {canReview && !receipt.is_approved && (
                      <Tooltip title={t('review')}>
                        <IconButton
                          color="warning"
                          component={RouterLink}
                          to={`/billing-receipts/review/${receipt.id}`}
                        >
                          <ReviewIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* Download button - disabled visual state if not approved and not creator/checker */}
                    <Tooltip title={
                      !receipt.is_approved && 
                      receipt.created_by !== user.id && 
                      !canReview ? t('cannotDownloadUnapprovedReceipt') : t('download')
                    }>
                      <span>
                        <IconButton
                          color="info"
                          onClick={() => handleDownloadClick(receipt)}
                          disabled={!receipt.is_approved && receipt.created_by !== user.id && !canReview}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    
                    {/* Delete button - only shown if user can delete this receipt */}
                    {canDelete(receipt) && (
                      <Tooltip title={t('delete')}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(receipt)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteReceiptConfirmation', { 
              id: receiptToDelete?.id 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Download Options Dialog */}
      <Dialog
        open={downloadOptionsDialogOpen}
        onClose={handleDownloadOptionsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('downloadOptions')}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {t('receiptID')}: {receiptToDownload?.id}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            {t('studentList')}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleDownloadDetailed}
            >
              {t('detailedView')}
            </Button>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleDownloadSummarized}
            >
              {t('summarizedView')}
            </Button>
          </Box>
          
          <Typography variant="subtitle2" gutterBottom>
            {t('accountDistribution')}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={handleDownloadDistributionDetailed}
            >
              {t('detailedDistribution')}
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={handleDownloadDistributionSummarized}
            >
              {t('summarizedDistribution')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadOptionsClose}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingReceiptsList;